// OrgTree — composition root for the top-down hierarchical org tree.
//
// Reads the org-graph payload, computes a Reingold-Tilford layout via
// `useTreeLayout`, and renders one `<TreeConnector>` per parent->child edge
// then one `<TreeNode>` per person. Connectors render first so the cards
// always sit on top.
//
// US1 ships fully-expanded with no interactivity. Wave 1 (US2-US5) plugs
// collapse, zoom/pan, selection, and search in at the four wire-in points
// marked below.
//
// Owns nothing beyond the team-colour map and the layout result — fetch /
// loading / error state live in `<OrgTreeTab>`.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IOrgGraphNode, IOrgGraphResponse } from '@skystone/shared';
import {
  ORPHAN_COLOR,
  ORPHAN_TEAM_ID,
  buildTeamColorMap,
  teamIdForNode,
} from '../org-graph/team-colors';
import { useZoomPan } from '../org-graph/hooks/useZoomPan';
import { TreeConnector } from './TreeConnector';
import { TreeNode } from './TreeNode';
import { GhostNode } from './GhostNode';
import { TreeControls } from './TreeControls';
import { PersonHoverCard } from '../people/PersonHoverCard';
import { useTreeCollapse } from './hooks/useTreeCollapse';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  VIRTUAL_ROOT_ID,
  computeHasReports,
  isGhostId,
  useTreeLayout,
} from './hooks/useTreeLayout';
import { FOCUS_ZOOM, TOP_BIAS, useTreeFocus } from './hooks/useTreeFocus';
import { useTreeKeyboardNav } from './hooks/useTreeKeyboardNav';

/** Fallback viewport height when the SVG hasn't been measured yet (e.g. in
 *  jsdom where getBoundingClientRect returns zeros). */
/** Fixed camera viewport in logical SVG units. The viewBox is intentionally
 *  STATIC (not derived from the tree's bounding box) so the d3-zoom transform
 *  is a real pan/zoom camera: `useTreeFocus` positions the current user / root
 *  within this window rather than the whole tree being scaled-to-fit (which
 *  made everything tiny — see PRODOPSAI-473). The aspect roughly matches the
 *  `h-[640px] w-full` SVG so content isn't letterboxed. */
const VIEWBOX_W = 1200;
const VIEWBOX_H = 700;

/** How long the search-navigation pulse-ring stays on the target card.
 *  Matches the `tree-node-highlight-pulse` keyframe duration in index.css. */
const HIGHLIGHT_DURATION_MS = 2000;

/**
 * Build a `id -> managerUpn` lookup over the supplied nodes. Used by
 * `expandPathTo` to walk an ancestor chain without re-scanning the array.
 */
function buildManagerIndex(
  nodes: readonly IOrgGraphNode[],
): Map<string, string | null> {
  const idx = new Map<string, string | null>();
  for (const n of nodes) idx.set(n.id, n.managerUpn);
  return idx;
}

interface OrgTreeProps {
  data: IOrgGraphResponse;
}

export function OrgTree({ data }: Readonly<OrgTreeProps>) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { transform, panTo, zoomTo } = useZoomPan(svgRef);

  const { collapsedIds, toggle, expandAll, collapseAll, hiddenChildCount } =
    useTreeCollapse(data);
  const { root, hasVirtualRoot } = useTreeLayout(data, collapsedIds);

  // Transient highlight that travels with the search-navigation flow. Cleared
  // by the effect below on a timer matching the CSS pulse duration.
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  useEffect(() => {
    if (highlightedNodeId === null) return undefined;
    const timer = setTimeout(() => {
      setHighlightedNodeId(null);
    }, HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [highlightedNodeId]);

  // Pending search-navigation target. `handleNavigateToNode` expands the
  // path to it and records the id here; the focus effect below pans + zooms
  // once the (possibly newly-expanded) layout actually contains the node.
  // This deferral is what makes navigation work for targets that were hidden
  // behind a collapsed ancestor at click time.
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  // Click on a tree node opens a `PersonHoverCard` popover anchored to the
  // node (PRODOPSAI-497). The popover owns its own open state, so OrgTree no
  // longer tracks a selected node id. Build the department id->label map once
  // so each wrapper can resolve its label without a per-render scan.
  const departmentMap = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    for (const d of data.departments) m.set(d.id, d.name);
    return m;
  }, [data.departments]);

  // The signed-in user is the unique node with `isCurrentUser === true`, or
  // null if no such node is present in the visible tree.
  const currentUserUpn = useMemo(() => {
    const me = data.nodes.find((n) => n.isCurrentUser);
    return me?.id ?? null;
  }, [data.nodes]);

  // Roving focus: exactly one card carries `tabIndex=0` at a time so Tab
  // lands inside the tree once. Defaults to the signed-in user so keyboard
  // users immediately start navigating from their own card. Updated by
  // useTreeKeyboardNav (arrow keys) and TreeNode's onFocus (Tab-into or
  // mouse click).
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(currentUserUpn);
  // Re-seed the focused id once data finishes loading and the current user
  // becomes known (e.g. async data load after the OrgTree mounted with an
  // empty payload). Without this, focus stays `null` and Tab falls back
  // to browser-default order.
  useEffect(() => {
    if (focusedNodeId === null && currentUserUpn !== null) {
      setFocusedNodeId(currentUserUpn);
    }
  }, [currentUserUpn, focusedNodeId]);

  useTreeFocus({
    layout: root,
    currentUserUpn,
    panTo,
    zoomTo,
    viewportWidth: VIEWBOX_W,
    viewportHeight: VIEWBOX_H,
    isReady: root !== null,
  });

  // Manager index over the FULL data so expandPathTo can walk an ancestor
  // chain even when ancestors are currently hidden by collapses.
  const managerIndex = useMemo(
    () => buildManagerIndex(data.nodes),
    [data.nodes],
  );

  /**
   * Walk up from `nodeId` via `managerUpn`, toggling every ancestor that is
   * currently collapsed so the target node becomes visible. Cycle-safe.
   */
  const expandPathTo = useCallback(
    (nodeId: string) => {
      const visited = new Set<string>();
      let cursor: string | null = managerIndex.get(nodeId) ?? null;
      while (cursor !== null && !visited.has(cursor)) {
        visited.add(cursor);
        if (collapsedIds.has(cursor)) toggle(cursor);
        cursor = managerIndex.get(cursor) ?? null;
      }
    },
    [managerIndex, collapsedIds, toggle],
  );

  /** Pan + zoom the viewport so the supplied laid-out point is centred
   *  horizontally and biased toward the top of the fixed camera viewport,
   *  using the same framing as `useTreeFocus`. With a static viewBox, a
   *  logical point P maps to screen S = P*k + translate, so to put P at
   *  viewport coordinate V we set translate = V - P*k. */
  const focusOnPoint = useCallback(
    (targetX: number, targetY: number) => {
      zoomTo(FOCUS_ZOOM);
      panTo(
        VIEWBOX_W / 2 - targetX * FOCUS_ZOOM,
        VIEWBOX_H * TOP_BIAS - targetY * FOCUS_ZOOM,
      );
    },
    [panTo, zoomTo],
  );

  const handleNavigateToNode = useCallback(
    (nodeId: string) => {
      // Expand any collapsed ancestors first, then defer the pan/zoom to the
      // focus effect below. The expansion changes `collapsedIds`, which
      // re-runs the layout; once the target appears in the refreshed
      // `root.descendants()` the effect focuses it. Deferring (rather than
      // reading the stale layout synchronously) is what lets us navigate to
      // results that were hidden behind a collapsed branch at click time.
      expandPathTo(nodeId);
      setPendingFocusId(nodeId);
      setHighlightedNodeId(nodeId);
    },
    [expandPathTo],
  );

  // Focus the pending search target as soon as it is present in the layout.
  // Runs on every layout/pending change; clears the pending id once focused
  // so it fires exactly once per navigation.
  useEffect(() => {
    if (pendingFocusId === null || root === null) return;
    const target = root.descendants().find((d) => d.data.id === pendingFocusId);
    if (target === undefined) return;
    focusOnPoint(target.x, target.y);
    setPendingFocusId(null);
  }, [pendingFocusId, root, focusOnPoint]);

  const handleCentreOnMe = useCallback(() => {
    if (currentUserUpn === null || root === null) return;
    const target = root.descendants().find((d) => d.data.id === currentUserUpn);
    if (target === undefined) return;
    focusOnPoint(target.x, target.y);
  }, [currentUserUpn, root, focusOnPoint]);

  // Build the team-colour map from the full input data — the layout might
  // drop orphans but the colour assignment still uses the canonical roster.
  // We derive `hasReports` from each node's `managerUpn` (via the shared
  // helper) so it cannot disagree with `useTreeLayout`'s own orphan
  // detection — see sonar-review feedback on PRODOPSAI-467.
  const hasReports = useMemo(() => computeHasReports(data.nodes), [data.nodes]);
  const teamColorMap = useMemo(
    () => buildTeamColorMap(data.nodes, hasReports),
    [data.nodes, hasReports],
  );
  const colorForNode = (node: IOrgGraphNode): string => {
    const teamId = teamIdForNode(node, hasReports);
    if (teamId === ORPHAN_TEAM_ID) return ORPHAN_COLOR;
    return teamColorMap.get(teamId) ?? 'var(--color-muted)';
  };

  // Keyboard navigation: arrow keys move focus between adjacent nodes,
  // Space toggles collapse on the focused card. Enter is left to the
  // TreeNode's own onSelect path (drawer-open). Hook is called before the
  // empty-state early return so the rules of hooks are honoured.
  const { handleKeyDown: handleTreeKeyDown } = useTreeKeyboardNav({
    layout: root,
    hasVirtualRoot,
    focusedNodeId,
    setFocusedNodeId,
    onToggle: toggle,
    svgRef,
  });

  if (root === null) {
    return (
      <div
        className="rounded-md border border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground"
        data-testid="org-tree-empty"
      >
        No hierarchy to display.
      </div>
    );
  }

  // Collect every descendant once so the renderer can iterate in pre-order.
  const allDescendants = root.descendants();
  // Renderable nodes exclude the synthetic virtual root.
  const renderableNodes = allDescendants.filter(
    (n) => !(hasVirtualRoot && n.data.id === VIRTUAL_ROOT_ID),
  );

  // Ghost-manager bridge nodes are not real people — exclude them from the
  // count surfaced to assistive tech.
  const peopleCount = renderableNodes.filter((n) => !isGhostId(n.data.id)).length;
  const ariaLabel = `Organisation tree, ${peopleCount} people across ${data.departments.length} departments`;

  // Pre-compute "this id has at least one direct report in the full data"
  // so we can set `aria-expanded` only on cards that actually have
  // children. A card with no children must omit aria-expanded entirely per
  // the WAI-ARIA tree pattern.
  const hasChildrenInData = (id: string): boolean => hasReports.has(id);

  // Resolve the effective focus target. We prefer the user-driven
  // `focusedNodeId` when it points at a visible card; otherwise we fall
  // back to the first renderable node so Tab always lands somewhere
  // inside the tree (rather than every card having tabIndex=-1).
  const renderableIds = renderableNodes.map((n) => n.data.id);
  const effectiveFocusedId = (() => {
    if (focusedNodeId !== null && renderableIds.includes(focusedNodeId)) {
      return focusedNodeId;
    }
    return renderableIds[0] ?? null;
  })();

  return (
    <div className="space-y-3">
      <TreeControls
        nodes={data.nodes}
        currentUserUpn={currentUserUpn}
        onNavigateToNode={handleNavigateToNode}
        onCentreOnMe={handleCentreOnMe}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
      />
      <div className="relative w-full overflow-auto rounded-lg border border-border bg-gradient-to-b from-background to-muted/30">
      <svg
        ref={svgRef}
        role="tree"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        className="h-[640px] w-full cursor-grab active:cursor-grabbing"
        data-testid="org-tree-svg"
      >
        <g
          data-testid="tree-zoom-layer"
          transform={`translate(${transform.x}, ${transform.y}) scale(${transform.k})`}
        >
          <g data-testid="tree-connector-layer" aria-hidden="true">
            {renderableNodes.map((child) => {
              const parent = child.parent;
              if (parent === null) return null;
              // Skip the connector segment that would join the synthetic root
              // to the real roots — there is no parent card to start from.
              if (hasVirtualRoot && parent.data.id === VIRTUAL_ROOT_ID) {
                return null;
              }
              return (
                <TreeConnector
                  key={`${parent.data.id}->${child.data.id}`}
                  sourceX={parent.x}
                  sourceY={parent.y + NODE_HEIGHT}
                  targetX={child.x}
                  targetY={child.y}
                  // Dash the segment out of a ghost manager to signal the
                  // reporting line leaves the P&E org.
                  dashed={isGhostId(parent.data.id)}
                />
              );
            })}
          </g>
          <g data-testid="tree-node-layer">
            {renderableNodes.map((n) => {
              const id = n.data.id;
              // Ghost-manager bridge nodes render as a greyed, non-interactive
              // card (no collapse, no drawer, no team colour, no keyboard).
              if (isGhostId(id)) {
                return (
                  <GhostNode
                    key={id}
                    name={n.data.displayName}
                    x={n.x - NODE_WIDTH / 2}
                    y={n.y}
                  />
                );
              }
              const isCollapsed = collapsedIds.has(id);
              const childCount = hiddenChildCount(id);
              const hasChildren = hasChildrenInData(id);
              // d3-hierarchy depth: virtual root is depth 0, real roots
              // depth 1, etc. ARIA aria-level is 1-indexed from the
              // user-visible top of the tree, so we subtract one when a
              // virtual root is present to keep real roots at level 1.
              const ariaLevel = hasVirtualRoot ? n.depth : n.depth + 1;
              const departmentLabel =
                n.data.departmentId === null
                  ? null
                  : departmentMap.get(n.data.departmentId) ?? null;
              return (
                <PersonHoverCard
                  key={id}
                  node={n.data}
                  departmentLabel={departmentLabel}
                >
                  <g data-testid={`tree-node-trigger-${id}`}>
                    <TreeNode
                      node={n.data}
                      x={n.x - NODE_WIDTH / 2}
                      y={n.y}
                      color={colorForNode(n.data)}
                      hiddenCount={childCount}
                      onToggle={() => toggle(id)}
                      isHighlighted={highlightedNodeId === id}
                      onKeyDown={handleTreeKeyDown}
                      onFocus={setFocusedNodeId}
                      ariaLevel={ariaLevel}
                      hasChildren={hasChildren}
                      isCollapsed={isCollapsed}
                      focused={effectiveFocusedId === id}
                    />
                  </g>
                </PersonHoverCard>
              );
            })}
          </g>
        </g>
      </svg>
      </div>
    </div>
  );
}
